import React from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Fade } from "react-awesome-reveal";
import { MediaOptions } from "@app/components/base-page/models";
import { useGenericData } from "@app/hooks/use-generic-data/useGenericData";
import { DEFAULT_FADE_DURATION } from "@app/constants";

const DEFAULT_BG_POSITION = 50;
const DEFAULT_OVERLAY = 30;

type Props = {
  title: string;
  subtitle: string;
  image: string;
  mediaOptions?: MediaOptions[];
  btn1: string;
  handleHeroButton: React.MouseEventHandler;
};

export const EventTitle: React.FC<Props> = ({
  title,
  subtitle,
  image,
  mediaOptions,
  btn1,
  handleHeroButton,
}) => {
  const imageOptions = mediaOptions?.[0];
  const { notificationBar } = useGenericData();

  return (
    <Fade duration={DEFAULT_FADE_DURATION}>
      <div className={classNames("relative mb-20", "lg:mb-25", "xxl:mb-40")}>
        <div
          className={classNames({
            "h-[calc(100vh-32px)]": notificationBar?.isEnabled,
            "h-screen": !notificationBar?.isEnabled,
          })}
        >
          <GatsbyImage
            image={getGatsbyImage(image, { layout: "fullWidth" })}
            alt={title}
            className="h-full w-full object-cover"
            imgStyle={{
              objectPosition: `${imageOptions?.xAxis ?? DEFAULT_BG_POSITION}% ${imageOptions?.yAxis ?? DEFAULT_BG_POSITION}%`,
            }}
          />
        </div>
        <div
          className={classNames("absolute bottom-0 left-0 right-0 top-0")}
          style={{
            backgroundColor: `rgba(0, 0, 0, ${Number(imageOptions?.overlay ?? DEFAULT_OVERLAY) / 100})`,
          }}
        ></div>
        <div
          className={classNames(
            "absolute left-0 top-0 flex h-full w-full items-end justify-center",
            "lg:justify-start",
            "xxl:items-center xxl:justify-center",
          )}
        >
          <div
            className={classNames(
              "px-5 py-20 text-center",
              "lg:max-w-3xl lg:px-15 lg:py-25 lg:text-left",
              "xxl:max-w-4xl xxl:px-0 xxl:py-0 xxl:text-center",
            )}
          >
            <h1
              className={classNames(
                "mb-5 text-large text-primary-content",
                "lg:text-xx-large",
                "xxl:text-huge",
              )}
            >
              {title}
            </h1>
            <p className="mb-5 text-regular text-primary-content">{subtitle}</p>
            <button
              className="text-regular text-primary-content [&>svg.svg-inline--fa]:align-middle"
              onClick={handleHeroButton}
            >
              {btn1}
              <FontAwesomeIcon
                icon={faChevronRight}
                className="fa-2xs ml-2.5"
              />
            </button>
          </div>
        </div>
      </div>
    </Fade>
  );
};
